import PropTypes from "prop-types"
import React from "react"

import { sanitize } from "../../utils/utils"
import FooterSection from "../WebsiteBuilderV2/components/FooterSection"
import HeaderSection from "../WebsiteBuilderV2/components/HeaderSection"
import ServicePractitioner from "../WebsiteBuilderV2/components/ServicePractitioner"
import { formatHeadingFontFamily } from "../WebsiteBuilderV2/utils/fontUtils"
import { scopePath } from "../WebsiteBuilderV2/utils/urlUtils"
import { MinimalWebsiteBuilderV2Provider } from "../WebsiteBuilderV2/WebsiteBuilderV2Context"

import { formatBlogPostDate } from "./blogDateUtils"
import BlogPostFooter from "./BlogPostFooter"

import "../../components/shared/wysiwyg.sass"

const BlogPostTags = ({ tags, textDark }) => {
  if (!tags?.length) return null

  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span
          key={tag}
          className="rounded-full px-3 py-1 text-sm font-medium"
          style={{ backgroundColor: "#e4eff6", color: textDark }}>
          {tag}
        </span>
      ))}
    </div>
  )
}

const BlogPostPage = ({ practice, post, theme, deviceType = "desktop", isPreview = false }) => {
  const headingFont = formatHeadingFontFamily(theme?.fonts?.heading)
  const textDark = theme?.colors?.textDark || "#55433f"
  const blogPageTitle = practice?.blogPageTitle || "Blog"
  const publishedLabel = formatBlogPostDate(post.publishedAt)

  const headerSection = { id: "blog-post-header", settings: { showNavigation: true, showCTA: true } }
  const footerSection = { id: "blog-post-footer", settings: {} }

  return (
    <MinimalWebsiteBuilderV2Provider practice={practice} theme={theme}>
      <div className="website-builder-v2-public min-h-screen bg-white">
        <HeaderSection section={headerSection} practice={practice} theme={theme} deviceType={deviceType} />

        {isPreview && (
          <div className="fixed inset-x-0 top-0 z-50 bg-orange-light px-4 py-2 text-center text-sm font-bold text-orange">
            Preview mode — this draft is only visible to you.
          </div>
        )}

        <main className={`mx-auto max-w-3xl px-5 pb-24 ${isPreview ? "pt-12" : "pt-8"}`}>
          <a
            href={scopePath("/blog", practice)}
            className="mb-6 inline-block text-sm font-semibold text-teal hover:underline">
            ← Back to {blogPageTitle}
          </a>

          {post.photoUrl && <img src={post.photoUrl} alt="" className="mb-8 w-full rounded-xl object-contain" />}

          {publishedLabel && (
            <time
              dateTime={post.publishedAt}
              className="mb-4 block text-sm font-medium"
              style={{ color: textDark, opacity: 0.7 }}>
              {publishedLabel}
            </time>
          )}

          <h1
            className="mb-4 text-4xl font-bold leading-tight"
            style={{ color: textDark, fontFamily: headingFont, fontWeight: theme?.fonts?.headingWeight }}>
            {post.title}
          </h1>

          {post.subtitle && (
            <p className="mb-6 text-xl leading-relaxed" style={{ color: textDark, opacity: 0.85 }}>
              {post.subtitle}
            </p>
          )}

          <BlogPostTags tags={post.tags} textDark={textDark} />

          {post.content && (
            <div
              className="wysiwyg-content mb-12 max-w-none text-lg leading-relaxed"
              style={{ color: textDark }}
              dangerouslySetInnerHTML={{ __html: sanitize(post.content) }}
            />
          )}

          <BlogPostFooter practice={practice} theme={theme} />

          <div className="mt-12">
            <ServicePractitioner practice={practice} theme={theme} />
          </div>
        </main>

        <FooterSection section={footerSection} practice={practice} theme={theme} deviceType={deviceType} />
      </div>
    </MinimalWebsiteBuilderV2Provider>
  )
}

BlogPostPage.propTypes = {
  practice: PropTypes.object.isRequired,
  post: PropTypes.object.isRequired,
  theme: PropTypes.object,
  deviceType: PropTypes.string,
  isPreview: PropTypes.bool
}

export default BlogPostPage
